import {
  type AdminConversationInboxItem,
  type AdminLeadAssignee,
  type ConversationHandoffState,
  type ConversationStatus,
} from "@whitehaven/contracts";
import {
  ContentState,
  DataTable,
  FormField,
  Select,
  StatusMessage,
  StatusPill,
  type DataTableColumn,
} from "@whitehaven/ui";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import {
  ADMIN_CONVERSATION_UNASSIGNED_VALUE,
  adminConversationHandoffLabels,
  adminConversationStatusLabels,
  getAdminConversationHandoffActionLabel,
  getAdminConversationHandoffTarget,
  getAdminConversationStatusTargets,
} from "./admin-conversation-inbox-model";

export type AdminConversationPendingMutationKind =
  | "assignment"
  | "status"
  | "handoff";

export interface AdminConversationPendingMutation {
  readonly conversationId: AdminConversationInboxItem["conversationId"];
  readonly kind: AdminConversationPendingMutationKind;
}

export type AdminConversationDirectoryState =
  | "loading"
  | "ready"
  | "error"
  | "unavailable";

export interface AdminConversationFeedback {
  readonly tone: "positive" | "critical";
  readonly message: string;
}

export interface AdminConversationInboxViewProps {
  readonly items: readonly AdminConversationInboxItem[];
  readonly assignees: readonly AdminLeadAssignee[];
  readonly directoryState: AdminConversationDirectoryState;
  readonly nextCursor: string | null;
  readonly refreshing: boolean;
  readonly loadingMore: boolean;
  readonly pendingMutation: AdminConversationPendingMutation | null;
  readonly refreshRequired: boolean;
  readonly feedback: AdminConversationFeedback | null;
  readonly canMutateAssignment: boolean;
  readonly canMutateStatus: boolean;
  readonly canMutateHandoff: boolean;
  readonly onRefresh: () => void;
  readonly onLoadMore: () => void;
  readonly onRetryAssignees: () => void;
  readonly onAssign: (
    item: AdminConversationInboxItem,
    targetUserId: string | null,
  ) => void;
  readonly onStatusChange: (
    item: AdminConversationInboxItem,
    targetStatus: ConversationStatus,
  ) => void;
  readonly onHandoffChange: (
    item: AdminConversationInboxItem,
    targetState: ConversationHandoffState,
  ) => void;
}

const conversationDateFormatter = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Pacific/Honolulu",
});

export function formatAdminConversationDate(value: string): string | null {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? conversationDateFormatter.format(date)
    : null;
}

function conversationLabel(item: AdminConversationInboxItem): string {
  const suffix = item.conversationId.slice(-6);
  return `Conversation ending ${suffix}`;
}

function ConversationIdentity({
  item,
}: {
  readonly item: AdminConversationInboxItem;
}) {
  return (
    <div className="admin-conversation-inbox__identity">
      <strong>{conversationLabel(item)}</strong>
      {item.leadId ? (
        <a href={`/admin/crm/leads/${encodeURIComponent(item.leadId)}`}>
          Open linked lead
        </a>
      ) : (
        <span>No linked lead</span>
      )}
    </div>
  );
}

function ConversationActivity({
  item,
}: {
  readonly item: AdminConversationInboxItem;
}) {
  const formatted = item.lastMessageAt
    ? formatAdminConversationDate(item.lastMessageAt)
    : null;

  return (
    <div className="admin-conversation-inbox__activity">
      <span>
        {item.messageCount} {item.messageCount === 1 ? "message" : "messages"}
      </span>
      {item.lastMessageAt && formatted ? (
        <time dateTime={item.lastMessageAt}>Last activity {formatted}</time>
      ) : (
        <span>No message activity recorded</span>
      )}
    </div>
  );
}

function currentAssigneeLabel(
  item: AdminConversationInboxItem,
  assignees: readonly AdminLeadAssignee[],
): string {
  if (item.assignedUserId === null) return "Unassigned";
  return (
    assignees.find(({ userId }) => userId === item.assignedUserId)
      ?.displayLabel ?? "Recorded assignee unavailable"
  );
}

function MutationForm({
  children,
  label,
  onSubmit,
}: {
  readonly children: ReactNode;
  readonly label: string;
  readonly onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <form
      className="admin-conversation-inbox__mutation-form"
      aria-label={label}
      onSubmit={onSubmit}
    >
      {children}
    </form>
  );
}

interface ConversationControlsProps {
  readonly item: AdminConversationInboxItem;
  readonly assignees: readonly AdminLeadAssignee[];
  readonly directoryState: AdminConversationDirectoryState;
  readonly busy: boolean;
  readonly refreshRequired: boolean;
  readonly canMutateAssignment: boolean;
  readonly canMutateStatus: boolean;
  readonly canMutateHandoff: boolean;
  readonly onAssign: (
    item: AdminConversationInboxItem,
    targetUserId: string | null,
  ) => void;
  readonly onStatusChange: (
    item: AdminConversationInboxItem,
    targetStatus: ConversationStatus,
  ) => void;
  readonly onHandoffChange: (
    item: AdminConversationInboxItem,
    targetState: ConversationHandoffState,
  ) => void;
}

function ConversationControls({
  item,
  assignees,
  directoryState,
  busy,
  refreshRequired,
  canMutateAssignment,
  canMutateStatus,
  canMutateHandoff,
  onAssign,
  onStatusChange,
  onHandoffChange,
}: ConversationControlsProps) {
  const targets = getAdminConversationStatusTargets(item.status);
  const [selectedAssignee, setSelectedAssignee] = useState(
    item.assignedUserId ?? ADMIN_CONVERSATION_UNASSIGNED_VALUE,
  );
  const [selectedStatus, setSelectedStatus] = useState<ConversationStatus>(
    targets[0] ?? item.status,
  );

  useEffect(() => {
    setSelectedAssignee(
      item.assignedUserId ?? ADMIN_CONVERSATION_UNASSIGNED_VALUE,
    );
  }, [item.assignedUserId, item.assignmentRevision]);

  useEffect(() => {
    const nextTargets = getAdminConversationStatusTargets(item.status);
    setSelectedStatus(nextTargets[0] ?? item.status);
  }, [item.status, item.statusRevision]);

  const assignmentTarget =
    selectedAssignee === ADMIN_CONVERSATION_UNASSIGNED_VALUE
      ? null
      : selectedAssignee;
  const assignmentChanged = assignmentTarget !== item.assignedUserId;
  const currentAssigneeMissing =
    item.assignedUserId !== null &&
    !assignees.some(({ userId }) => userId === item.assignedUserId);
  const assignmentDisabled =
    busy ||
    refreshRequired ||
    directoryState !== "ready" ||
    !canMutateAssignment;
  const statusDisabled =
    busy || refreshRequired || !canMutateStatus || targets.length === 0;
  const handoffTarget = getAdminConversationHandoffTarget(item.handoffState);
  const handoffDisabled =
    busy || refreshRequired || !canMutateHandoff || handoffTarget === null;

  return (
    <div
      className="admin-conversation-inbox__controls"
      aria-busy={busy ? "true" : undefined}
    >
      <MutationForm
        label={`Change assignment for ${conversationLabel(item)}`}
        onSubmit={(event) => {
          event.preventDefault();
          if (!assignmentDisabled && assignmentChanged) {
            onAssign(item, assignmentTarget);
          }
        }}
      >
        <FormField
          id={`conversation-assignee-${item.conversationId}`}
          label="Assignment"
        >
          {(controlProps) => (
            <Select
              {...controlProps}
              value={selectedAssignee}
              disabled={assignmentDisabled}
              onChange={(event) =>
                setSelectedAssignee(event.currentTarget.value)
              }
            >
              <option value={ADMIN_CONVERSATION_UNASSIGNED_VALUE}>
                Unassigned
              </option>
              {currentAssigneeMissing && item.assignedUserId && (
                <option value={item.assignedUserId} disabled>
                  Current recorded assignee
                </option>
              )}
              {assignees.map((assignee) => (
                <option key={assignee.userId} value={assignee.userId}>
                  {assignee.displayLabel}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <button
          type="submit"
          disabled={assignmentDisabled || !assignmentChanged}
        >
          Confirm assignment
        </button>
      </MutationForm>

      <MutationForm
        label={`Change status for ${conversationLabel(item)}`}
        onSubmit={(event) => {
          event.preventDefault();
          if (!statusDisabled && selectedStatus !== item.status) {
            onStatusChange(item, selectedStatus);
          }
        }}
      >
        <FormField
          id={`conversation-status-${item.conversationId}`}
          label="Next status"
        >
          {(controlProps) => (
            <Select
              {...controlProps}
              value={selectedStatus}
              disabled={statusDisabled}
              onChange={(event) => {
                const value = event.currentTarget.value as ConversationStatus;
                if (
                  getAdminConversationStatusTargets(item.status).includes(value)
                ) {
                  setSelectedStatus(value);
                }
              }}
            >
              {targets.length === 0 ? (
                <option value={item.status}>No further transitions</option>
              ) : (
                targets.map((status) => (
                  <option key={status} value={status}>
                    {adminConversationStatusLabels[status]}
                  </option>
                ))
              )}
            </Select>
          )}
        </FormField>
        <button type="submit" disabled={statusDisabled}>
          Confirm status
        </button>
      </MutationForm>

      <div className="admin-conversation-inbox__handoff-control">
        <StatusPill
          tone={item.handoffState === "REQUESTED" ? "warning" : "neutral"}
        >
          {adminConversationHandoffLabels[item.handoffState]}
        </StatusPill>
        {handoffTarget === null ? (
          <span>Waiting for a guest handoff request.</span>
        ) : (
          <button
            type="button"
            disabled={handoffDisabled}
            onClick={() => onHandoffChange(item, handoffTarget)}
          >
            {getAdminConversationHandoffActionLabel(item.handoffState)}
          </button>
        )}
      </div>
    </div>
  );
}

export function AdminConversationInboxView({
  items,
  assignees,
  directoryState,
  nextCursor,
  refreshing,
  loadingMore,
  pendingMutation,
  refreshRequired,
  feedback,
  canMutateAssignment,
  canMutateStatus,
  canMutateHandoff,
  onRefresh,
  onLoadMore,
  onRetryAssignees,
  onAssign,
  onStatusChange,
  onHandoffChange,
}: AdminConversationInboxViewProps) {
  const busy = refreshing || loadingMore || pendingMutation !== null;

  if (items.length === 0) {
    return (
      <ContentState
        kind="empty"
        title="No conversations are available."
        actions={
          <button type="button" onClick={onRefresh}>
            Refresh conversations
          </button>
        }
      >
        <p>
          The authorized inbox request completed without returning any
          conversation records.
        </p>
      </ContentState>
    );
  }

  const columns: readonly DataTableColumn<AdminConversationInboxItem>[] = [
    {
      id: "conversation",
      header: "Conversation",
      rowHeader: true,
      render: (item) => <ConversationIdentity item={item} />,
    },
    {
      id: "state",
      header: "Current state",
      render: (item) => (
        <div className="admin-conversation-inbox__state">
          <StatusPill
            tone={
              item.status === "SPAM"
                ? "critical"
                : item.status === "WAITING_FOR_STAFF"
                  ? "warning"
                  : "neutral"
            }
          >
            {adminConversationStatusLabels[item.status]}
          </StatusPill>
          <span>{currentAssigneeLabel(item, assignees)}</span>
        </div>
      ),
    },
    {
      id: "activity",
      header: "Activity",
      render: (item) => <ConversationActivity item={item} />,
    },
    {
      id: "controls",
      header: "Authorized actions",
      render: (item) => (
        <ConversationControls
          item={item}
          assignees={assignees}
          directoryState={directoryState}
          busy={busy}
          refreshRequired={refreshRequired}
          canMutateAssignment={canMutateAssignment}
          canMutateStatus={canMutateStatus}
          canMutateHandoff={canMutateHandoff}
          onAssign={onAssign}
          onStatusChange={onStatusChange}
          onHandoffChange={onHandoffChange}
        />
      ),
    },
  ];

  return (
    <div
      className="admin-conversation-inbox"
      aria-busy={busy ? "true" : undefined}
    >
      <div className="admin-conversation-inbox__toolbar">
        <p>
          {items.length} loaded{" "}
          {items.length === 1 ? "conversation" : "conversations"}. The inbox
          shows operational metadata only.
        </p>
        <button type="button" disabled={busy} onClick={onRefresh}>
          {refreshing ? "Refreshing" : "Refresh conversations"}
        </button>
      </div>

      {directoryState === "loading" && (
        <ContentState
          kind="loading"
          headingLevel={2}
          title="Loading eligible assignees."
        />
      )}
      {directoryState === "error" && (
        <ContentState
          kind="error"
          headingLevel={2}
          title="Eligible assignees could not be loaded."
          actions={
            <button type="button" disabled={busy} onClick={onRetryAssignees}>
              Try assignee directory again
            </button>
          }
        >
          <p>Status and human follow-up controls remain independently gated.</p>
        </ContentState>
      )}
      {directoryState === "unavailable" && (
        <ContentState
          kind="warning"
          headingLevel={2}
          title="Assignment controls are unavailable."
        >
          <p>
            The protected assignee directory or assignment mutation service has
            not been connected.
          </p>
        </ContentState>
      )}

      {feedback && (
        <StatusMessage tone={feedback.tone} title={feedback.message} live />
      )}
      {refreshRequired && (
        <ContentState
          kind="error"
          headingLevel={2}
          title="Refresh is required before another change."
          actions={
            <button type="button" disabled={busy} onClick={onRefresh}>
              Refresh conversations
            </button>
          }
        >
          <p>
            A record changed or a response could not be verified. No further
            mutation is available until a fresh authorized read succeeds.
          </p>
        </ContentState>
      )}

      <DataTable
        regionLabel="Authorized conversation inbox"
        caption="Conversation assignment, status, handoff, and activity"
        columns={columns}
        rows={items}
        getRowKey={(item) => item.conversationId}
      />

      {nextCursor && (
        <button type="button" disabled={busy} onClick={onLoadMore}>
          {loadingMore ? "Loading conversations" : "Load more conversations"}
        </button>
      )}
    </div>
  );
}
